import React, { FC, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'components/ui';
import { Error, SuccessMessage } from 'components';
import { t } from 'utils';
import styles from './FileInput.module.scss';

export interface IFileInput {
  label: string | JSX.Element;
  onChange?: (image: any) => void;
  onBlur?: () => void;
  isError?: boolean;
  isSuccess?: boolean;
  placeholder?: string | JSX.Element;
  className?: string;
  disabled?: boolean;
  acceptFileTypes: string;
}

const FileInput: FC<IFileInput> = (props) => {
  const {
    label,
    onChange = () => {},
    onBlur = () => {},
    acceptFileTypes,
    placeholder = 'Select file...',
    className,
    isError,
    isSuccess,
    disabled,
  } = props;
  const [hasError, setHasError] = useState(isError);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const rootClassName = classNames(styles.root, className, { [styles.disabled]: disabled });
  const error = isError ?? hasError;

  const encodeImageFileAsURL = useCallback(
    (element) => {
      const file = element.files[0];
      const reader = new FileReader();

      reader.onloadend = function () {
        const { result } = reader;
        if (typeof result === 'string') {
          onChange(result);
          onBlur();
        }
      };

      reader.readAsDataURL(file);
    },
    [onBlur, onChange]
  );

  const handleInputChange = useCallback(
    (e) => {
      setHasError(false);
      const fileName = (e.target.files[0] || {}).name;
      if (!fileName.toLowerCase().match(/\.(jpg|jpeg|png)$/)) {
        setHasError(true);
        return false;
      }
      setFileName(fileName);
      encodeImageFileAsURL(e.target);
    },
    [encodeImageFileAsURL]
  );

  return (
    <div className={rootClassName}>
      <input
        id='file'
        className='visually-hidden'
        onChange={handleInputChange}
        type='file'
        ref={inputRef}
        accept={acceptFileTypes}
      />
      <Error className={styles.status} isError={error}>
        {t('logo.size.is.wrong')}
      </Error>
      <SuccessMessage className={styles.status} isSuccess={isSuccess}>
        {t('logo.size.is.correct')}
      </SuccessMessage>
      <label className={styles.input} htmlFor='file'>
        {label && <span className={styles.label}>{label}</span>}
        {placeholder && <span className={styles.placeholder}>{fileName || placeholder}</span>}
        <Button
          className={styles.button}
          color='dark'
          onClick={() => inputRef.current?.click()}
          inline
          type='button'
        >
          {t('browse')}
        </Button>
      </label>
    </div>
  );
};

export default FileInput;
